import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from '@mui/icons-material/Edit';
import { Button, Pagination } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom';
import ExpenseDocumentBox from '../HrOfficeExpanses/ExpenseDocumentBox';
import ExpenseApprovalFrom from './ExpenseApprovalFrom';
import { expenseApprovalData, hrOfficeApproveExpenseCleanUp } from 'app/redux/actions/subTaskAction';
import Swal from 'sweetalert2';
import { docBaseUrl } from 'app/services/config';

// Constants
const EXPENSE_LIMIT = 10;
const CHIP_COLORS = {
  'Requested': 'blue',
  'Approved': 'green',
  'Rejected': 'red',
  'default': 'gray'
};

// Helper function to create filter query string
const createFilterString = (filterModel) => {
  if (!filterModel?.items) return '';
  return filterModel.items
    .filter(item => item.field !== undefined)
    .map(item => `&${item.field}=${item.value || ''}`)
    .join('');
};

// Helper function for CSV conversion
const convertToCSV = (objArray) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  let csv = '';

  for (const row of array) {
    let line = '';
    for (const index in row) {
      if (line !== '') line += ',';
      let fieldValue = row[index];

      if (Array.isArray(fieldValue)) {
        fieldValue = fieldValue.join('\n');
      } else if (typeof fieldValue === 'string' && fieldValue.charAt(0) === '0') {
        fieldValue = `"${fieldValue}"`;
      }

      if (typeof fieldValue === 'string' && (fieldValue.includes(',') || fieldValue.includes('\n'))) {
        fieldValue = `"${fieldValue.replace(/"/g, '""')}"`;
      }

      line += fieldValue;
    }
    csv += line + '\r\n';
  }
  return csv;
};

// Helper function to handle CSV export
const exportCSVFile = (headers, items, fileTitle) => {
  if (headers) {
    items.unshift(headers);
  }

  const jsonObject = JSON.stringify(items);
  const csv = convertToCSV(jsonObject);
  const exportedFilenmae = `${fileTitle}.csv` || 'export.csv';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, exportedFilenmae);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilenmae);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export default function EmployeeExpenseApprove() {
  const dispatch = useDispatch();
  const { setAuthToken } = useJumboAuth();
  const roleCode = localStorage.getItem("roleCode");
  const { employeeOfficeExpensesData } = useSelector(state => state.typereducer);
  const { hideDialog, showDialog } = useJumboDialog();
  const [totalCount, setTotalCount] = React.useState(0);
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);

  // Use useSearchParams hook to manage URL query parameters
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial page and filters from URL or use defaults
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialFilterModel = JSON.parse(searchParams.get('filter') || '{}');
  const [page, setPage] = React.useState(initialPage);
  const [queryOptions, setQueryOptions] = React.useState({ filterModel: initialFilterModel });

  // Memoized rows to prevent re-computation on every render
  const rows = React.useMemo(() => {
    let _id = (page - 1) * EXPENSE_LIMIT + 1;
    return employeeOfficeExpensesData.map(item => ({
      id: _id++,
      employeeName: item?.employee_id?.name || '--',
      expenseId: item?.id,
      expenseAmount: item?.expense_amount,
      expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
      expenseDescription: item?.expense_description || '--',
      expenseNote: item?.expense_note || '--',
      expenseDoc: item?.bill_reciept_documents || [],
      status: item?.status || 'Requested',
      hrFinanceStatus: item?.hr_finance_status || '--',
      paymentStatus: item?.payment_status || 'Unpaid',
      paidBy: item?.paid_by || '--',
      paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
      actionRemark: item?.action_remark || '--',
      actionBy: item?.action_by?.name || '--',
      hrActionBy: item?.hr_action_by?.name || '--',
      company: item?.company || '--',
      projects: item?.projects || '--',
    }));
  }, [employeeOfficeExpensesData, page]);

  // Handler for opening expense document dialog
  const showExpenseDialog = React.useCallback((expenseDoc) => {
    showDialog({
      title: "Expense Documents",
      content: <ExpenseDocumentBox expenseDoc={expenseDoc} />
    });
  }, [showDialog]);

  // Callback to handle dialog updates and close
  const handleExpenseUpdate = React.useCallback(() => {
    hideDialog();
  }, [hideDialog]);

  // Handler for opening expense approval dialog
  const showApproveExpenseDialog = React.useCallback((expenseIds, hrFinanceStatus) => {
    showDialog({
      title: "Expense",
      content: <ExpenseApprovalFrom expenseIds={expenseIds} hrFinanceStatus={hrFinanceStatus} onSave={handleExpenseUpdate} />
    });
  }, [handleExpenseUpdate, showDialog]);

  // Handler for bulk approval
  const handleBulkApproval = () => {
    const expenseIds = rowSelectionModel.map(item => item.expenseId);
    showApproveExpenseDialog(expenseIds);
  };

  // Handler for exporting data to CSV
  const handleExport = React.useCallback(() => {
    const filterQuery = createFilterString(queryOptions.filterModel);

    subTaskService.getExpensesApprovals('EmployeeExpenseApprove', filterQuery)
      .then((data) => {
        const mockData = data?.data?.data?.map((item, index) => ({
          "id": index + 1,
          "employeeName": item?.employee_id?.name || '--',
          "status": item?.status || 'Requested',
          "hrFinanceStatus": item?.hr_finance_status || '--',
          "actionRemark": item?.action_remark || "--",
          "actionBy": item?.action_by?.name || '--',
          "hrActionBy": item?.hr_action_by?.name || '--',
          "paymentStatus": item?.payment_status || 'Unpaid',
          "paidBy": item?.paid_by || '--',
          "paymentDate": item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
          "expenseAmount": item?.expense_amount || '--',
          "expenseDate": moment(item?.expense_date).format('DD MMM YYYY') || '--',
          "company": item?.company || '--',
          "projects": item?.projects || '--',
          "expenseDescription": item?.expense_description || '--',
          "expenseNote": item?.expense_note || '--',
          "expenseDoc": item?.bill_reciept_documents?.map(doc => `${docBaseUrl}${doc?.base_url}/${doc?.key}`) || '--',
        }));

        const headers = {
          id: "ID", employeeName: "Employee Name", status: "Manager Status", hrFinanceStatus: "HR/Finance Status",
          actionRemark: "Remark", actionBy: "Approved By(Manager)", hrActionBy: "Approved By(hr/finance)",
          paymentStatus: "Payment Status", paidBy: "Paid From", paymentDate: "Payment Date",
          expenseAmount: "Expense Amount", expenseDate: "Expense Date", company: 'Company', projects: 'Projects', expenseDescription: "Description",
          expenseNote: "Note", expenseDoc: "Documents"
        };

        exportCSVFile(headers, mockData, 'Employee Expense Export');
      })
      .catch((err) => {
        if (err?.response?.status === 401) setAuthToken(null);
      });
  }, [queryOptions, setAuthToken]);

  // Handler for DataGrid's filter changes
  const onFilterChange = React.useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
    if (filterModel?.items?.[0]?.value !== undefined) {
      setPage(1); // Reset to page 1 on filter change
    }
  }, []);

  // Handler for pagination changes
  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  // Handler for DataGrid cell clicks
  const handleCellClick = (params) => {
    const { field, value } = params;
    if (!['id', '__check__', 'expenseDoc'].includes(field)) {
      Swal.fire({
        html: `<div style="white-space: pre-wrap;">${value}</div>`,
        showConfirmButton: false,
        customClass: { popup: 'my-custom-size' },
        didOpen: () => {
          const swalPopup = Swal.getPopup();
          if (swalPopup) swalPopup.style.width = '400px';
        },
      });
    }
  };

  // Effect to fetch data and update URL
  React.useEffect(() => {
    // Sync state to URL search parameters
    const params = {};
    if (page !== 1) {
      params.page = page;
    }
    if (Object.keys(queryOptions.filterModel).length > 0) {
      params.filter = JSON.stringify(queryOptions.filterModel);
    }
    setSearchParams(params, { replace: true });

    // Fetch data from API
    const offset = (page - 1) * EXPENSE_LIMIT;
    const filterString = createFilterString(queryOptions.filterModel);

    subTaskService.getExpensesApprovals('EmployeeExpenseApprove', offset, EXPENSE_LIMIT, filterString)
      .then((data) => {
        setTotalCount(data?.data?.count);
        dispatch(expenseApprovalData(data?.data?.data));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAuthToken(null);
        }
      });

    return () => {
      dispatch(hrOfficeApproveExpenseCleanUp());
    };
  }, [page, queryOptions, dispatch, setAuthToken, setSearchParams]);

  // Memoized column definitions
  const columns = React.useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      filterable: false,
      flex: 100,
      maxWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isEditable = params.row.status === 'Requested';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span>{params.value}</span>
            <NavLink>
              <EditIcon
                onClick={() => isEditable && showApproveExpenseDialog(params.row.expenseId, params.row.hrFinanceStatus)}
                style={{
                  width: '20px', height: '20px', marginLeft: '5px',
                  cursor: isEditable ? 'pointer' : 'not-allowed'
                }}
                color="action"
                disabled={!isEditable}
              />
            </NavLink>
          </div>
        );
      },
    },
    { field: 'employeeName', headerName: 'Employee Name', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    {
      field: 'status',
      headerName: 'Manager Status',
      sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      ),
    },
    {
      field: 'hrFinanceStatus',
      headerName: 'HR/Finance Status',
      sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      ),
    },
    { field: 'actionRemark', headerName: 'Remark', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'expenseAmount', headerName: 'Expense Amount', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'expenseDate', headerName: 'Expense Date', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'expenseDescription', headerName: 'Description', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'expenseNote', headerName: 'Note', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'paymentStatus', headerName: 'Payment Status', type: 'singleSelect', valueOptions: ['Paid', 'Unpaid'], sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      type: 'date',
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? null : date;
      },
    },
    { field: 'projects', headerName: 'Projects', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'company', headerName: 'Company', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    {
      field: 'expenseDoc',
      headerName: 'Documents',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params.row.expenseDoc.length > 0 && (
            <ArticleIcon
              onClick={() => showExpenseDialog(params.value)}
              style={{ width: '20px', height: '20px', marginLeft: '5px' }}
              color="action"
            />
          )}
        </div>
      ),
    },
  ], [showApproveExpenseDialog, showExpenseDialog]);

  return (
    <>
      <h3>Employee Expense</h3>
      <div style={{ height: '60px', width: '100%' }}>
        {rowSelectionModel.length > 0 && (
          <Button variant="contained" onClick={handleBulkApproval}>
            Approve/Reject
          </Button>
        )}
        <Button onClick={handleExport} sx={{ float: 'right' }} variant="contained" color='secondary'>
          Export Expenses
        </Button>
      </div>
      <div style={{ height: '100%', width: '100%' }}>
        <DataGridPro
          checkboxSelection
          isRowSelectable={(params) => !['Approved', 'Rejected', 'Cancelled'].includes(params.row.status)}
          sx={{ backgroundColor: '#F6F4FA' }}
          rows={rows}
          columns={columns}
          pageSize={EXPENSE_LIMIT}
          autoPageSize
          autoHeight
          density='standard'
          filterMode='server'
          onFilterModelChange={onFilterChange}
          hideFooter
          onRowSelectionModelChange={(newRowSelectionModel) => {
            const selectedRows = newRowSelectionModel.map(id => rows.find(row => row.id === id));
            setRowSelectionModel(selectedRows);
          }}
          rowSelectionModel={rowSelectionModel?.map(i => i.id)}
          onCellClick={handleCellClick}
        />
        <Pagination
          count={Math.ceil(totalCount / EXPENSE_LIMIT)}
          sx={{ mt: 3, float: 'right', color: "#F6F4FA" }}
          page={page}
          onChange={handlePaginationChange}
        />
      </div>
    </>
  );
}